/**
 * PresenceContext — live presence map for the caller's connections (Phase C).
 *
 * Seeds from REST and (once the presence hub is wired) applies live
 * `ReceivePresence` events. A non-owner never sees another user's 'invisible'
 * status — the server has already stripped it before it reaches this map.
 *
 * Inert until `social.dm_presence` is on.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useUser } from './UserContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { presenceService } from '../services/api/presenceService';
import { messagingSignalRService } from '../services/signalr/MessagingSignalRService';
import type { ConnectionPresence } from '../types/social';

interface PresenceContextValue {
  /** profileId → latest known presence. */
  byProfile: Record<string, ConnectionPresence>;
  invisible: boolean;
  presenceOf: (profileId: string) => ConnectionPresence | undefined;
  setInvisible: (invisible: boolean) => Promise<void>;
  /** Apply a live presence update (called by the hub handler). */
  apply: (presence: ConnectionPresence) => void;
  refresh: () => Promise<void>;
}

const PresenceContext = createContext<PresenceContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function PresenceProvider({ children }: ProviderProps) {
  const { user } = useUser();
  const enabled = useFeatureFlag('social.dm_presence');
  const [byProfile, setByProfile] = useState<Record<string, ConnectionPresence>>({});
  const [invisible, setInvisibleState] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !user?.id) {
      setByProfile({});
      return;
    }
    try {
      const [list, inv] = await Promise.all([
        presenceService.getConnectionsPresence(),
        presenceService.getInvisible(),
      ]);
      setByProfile(Object.fromEntries(list.map((p) => [p.profileId, p])));
      setInvisibleState(inv);
    } catch {
      // Presence is best-effort — a failed seed leaves the map empty rather
      // than surfacing an error to the whole app.
      setByProfile({});
    }
  }, [enabled, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const apply = useCallback((presence: ConnectionPresence) => {
    setByProfile((m) => ({ ...m, [presence.profileId]: presence }));
  }, []);

  // Subscribe to live presence pushes on the shared hub. setEventHandlers
  // merges, so registering here never clobbers ActivityContext's invite
  // handlers. Only wire while the flag is on.
  useEffect(() => {
    if (!enabled) return;
    messagingSignalRService.setEventHandlers({ onReceivePresence: apply });
    return () => {
      messagingSignalRService.setEventHandlers({ onReceivePresence: undefined });
    };
  }, [enabled, apply]);

  const setInvisible = useCallback(async (next: boolean) => {
    await presenceService.setInvisible(next);
    // Mirror to the hub for immediate fan-out; REST is the source of truth.
    void messagingSignalRService.setInvisible(next);
    setInvisibleState(next);
  }, []);

  const presenceOf = useCallback(
    (profileId: string) => byProfile[profileId],
    [byProfile]
  );

  const value = useMemo<PresenceContextValue>(
    () => ({ byProfile, invisible, presenceOf, setInvisible, apply, refresh }),
    [byProfile, invisible, presenceOf, setInvisible, apply, refresh]
  );

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

export function usePresence(): PresenceContextValue {
  const ctx = useContext(PresenceContext);
  if (!ctx) throw new Error('usePresence must be used within a PresenceProvider');
  return ctx;
}
